import { request } from 'Features/auth/axios-client'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { PlannedProductionData } from './PlannedProduction.types'

export type SkuEditEntry = {
  workCentreCode: string
  workCentreKey: number
  skuCode: string
  skuKey: number
  baseSourceSiteCode: string
  baseSourceSiteKey: number
  maxWeekEditedIndex: number | null
  weekValues: Record<string, number>
}

export type FreezeEditedPayload = {
  edits: SkuEditEntry[]
}

export type FreezeAllMixedPayload = {
  minWeeks: number
  rows: SkuEditEntry[]
}

const fetchPlannedProduction = async (planId: string) => {
  const data = request({
    url: `/operational-plan/${planId}/planned-production`,
    method: 'GET',
  })
  return data
}

const savePlannedProduction = async (
  planId: string,
  data: PlannedProductionData[],
  selectedWeeks: number[]
) => {
  const response = request({
    url: `/operational-plan/${planId}/planned-production`,
    method: 'POST',
    data: { data, selectedWeeks },
  })
  return response
}

const savePlannedProductionFreezeEdited = async (
  planId: string,
  payload: FreezeEditedPayload
) => {
  const response = request({
    url: `/operational-plan/${planId}/planned-production/freeze-edited`,
    method: 'POST',
    data: payload,
  })
  return response
}

const savePlannedProductionFreezeAllMixed = async (
  planId: string,
  payload: FreezeAllMixedPayload
) => {
  const response = request({
    url: `/operational-plan/${planId}/planned-production/freeze-all-mixed`,
    method: 'POST',
    data: payload,
  })
  return response
}

const uploadPlannedProductionFile = async (planId: string, file: File) => {
  const formData = new FormData()
  formData.append('file', file)

  const response = request({
    url: `/operational-plan/${planId}/planned-production/upload`,
    method: 'POST',
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })
  return response
}

export const usePlannedProduction = (planId: string) => {
  return useQuery(
    ['PlannedProduction', planId],
    () => fetchPlannedProduction(planId),
    {
      select: ({ data }) => ({
        data: data?.data ?? [],
        weeks: data?.weeks ?? [],
      }),
      enabled: !!planId,
      cacheTime: 0,
    }
  )
}

export const useSavePlannedProduction = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      planId,
      data,
      selectedWeeks,
    }: {
      planId: string
      data: PlannedProductionData[]
      selectedWeeks: number[]
    }) => savePlannedProduction(planId, data, selectedWeeks),
    {
      onSuccess: (_, { planId }) => {
        // queryClient.invalidateQueries(['PlannedProduction', planId])
      },
    }
  )
}

export const useSavePlannedProductionFreezeEdited = () => {
  return useMutation(
    ({ planId, payload }: { planId: string; payload: FreezeEditedPayload }) =>
      savePlannedProductionFreezeEdited(planId, payload)
  )
}

export const useSavePlannedProductionFreezeAllMixed = () => {
  return useMutation(
    ({ planId, payload }: { planId: string; payload: FreezeAllMixedPayload }) =>
      savePlannedProductionFreezeAllMixed(planId, payload)
  )
}

export const useUploadPlannedProduction = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({ planId, file }: { planId: string; file: File }) =>
      uploadPlannedProductionFile(planId, file),
    {
      onSuccess: (_, { planId }) => {
        //queryClient.invalidateQueries(['PlannedProduction', planId])
      },
    }
  )
}
